import React, { useState, useEffect, useMemo, useCallback } from "react";

// @mui material components
import Divider from "@mui/material/Divider";
import { v4 as uuidv4 } from "uuid";
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";

// Custom styles for the Modal
import ModalRoot from "containers/modal/ModalRoot";
import { Alert, Button, Icon } from "@mui/material";
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid";
import recipeChart from "../modal/diet/recipeChart";
import Createable from "containers/Select";
import RichTextEditor from "components/RichTextEditor";
import { getWeeksArray } from "utils/common";
import CustomeDatepicker from "containers/Datepicker";

import { doc, getDoc } from "firebase/firestore";
import moment from "moment";
import { firestore } from "firebaseConfig";
import { TailSpin } from "react-loader-spinner";
import CustomButton from "containers/CustomButton";

import { getUserRecipes, AddDiet, EditDietChart } from "services/dietchart";
import { getClientDietChartForDate } from "services/dietchart";
import ModalHeader from "containers/modal-header";
import { useSelector, useDispatch } from "react-redux";
import {
  userSelector,
  getSavedDietChartTemplates,
} from "store/features/authSlice";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeselectIcon from "@mui/icons-material/Deselect";
import { AddRecipe } from "store/features/recipes";
import ApprovalToggle from "../ChartTable/components/DietRow/ApprovalToggle";
import { getClientDetails } from "store/features/clientDetails";
import { clientDetailsSelector } from "store/features/clientDetails";
import WarningBox from "components/WarningBox/WarningBox";
import { chartTypes } from "utils/common";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SuiButton from "components/SuiButton";

const defaultFormData = {
  clientName: null,
  from: new Date().toISOString().split("T")[0],
};

const PlanDetails = ({
  open,
  clients,
  selectedItem,
  setSelectedItem,
  client,
  setMode,
  editValue,
  mode,
  setSearchParams,
  setFormData,
  formData,
  dietChartTemplates,
  setDietPlan,
  schedules,
  selectedClientv1,
  setClientActive,
  isServiceActive,
}) => {
  const dispatch = useDispatch();

  const errorDefault = useMemo(() => {
    return {
      clientName: null,
      date: null,
      notes: null,
      chartType: null,
      weekPlan: null,
    };
  }, []);

  const [errorMessage, setErrorMessage] = useState(errorDefault);
  const [selectedTemplate, setSelectedTemplate] = useState({
    label: "",
    value: {},
  });
  const [note, setNote] = useState("");
  const [isClientTermsAccepted, setIsClientTermsAccepted] = useState(0);
  const [loader, setLoader] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [includeDates, setIncludeDates] = useState([]);
  const [dietLoader, setDietLoader] = useState(false);
  const [dietChartTemplateNames, setDietChartTemplateNames] = useState([]);
  const [defaultDietObj, setDefaultDietObj] = useState(false);

  useEffect(() => {
    if (selectedClientv1) {
      setFormData({ ...formData, clientName: selectedClientv1 });
    }
  }, [selectedClientv1]);

  useEffect(() => {
    if (schedules.length > 0) {
      const dynamicDietChartObj = {};

      schedules.forEach((schedule) => {
        const key = schedule.normalizedId || schedule.id;
        dynamicDietChartObj[key] = [];
      });

      setDefaultDietObj(dynamicDietChartObj);
    }
  }, [schedules]);

  useEffect(() => {
    if (dietChartTemplates && schedules) {
      const templateNames = dietChartTemplates.map((template) => {
        const updatedDietPlan = {};

        schedules.forEach((schedule) => {
          const key = schedule.normalizedId || schedule.id;
          const items = template.dietPlan?.[key] || [];

          const updatedItems = items.map((item) => ({
            ...item,
            name:
              typeof item.name === "string"
                ? item.name
                : item.name?.label || item.name?.value || "",
            time: schedule.time,
          }));

          updatedDietPlan[key] = updatedItems;
        });

        return {
          label: template.templateName,
          value: {
            dietPlan: updatedDietPlan,
            chartType: template.chartType,
            weekPlan: template.weekPlan,
            note: template.note,
          },
        };
      });

      setDietChartTemplateNames(templateNames);
    }
  }, [dietChartTemplates, schedules]);

  async function getClientDietChart(date) {
    try {
      const dates = includeDates.filter((item) => {
        if (item.date) {
          const itemDate = moment(item.date);
          return itemDate.isSame(moment(date), "day");
        }
        const from = moment(item.from.toDate?.() || item.from);
        const to = moment(item.to.toDate?.() || item.to);
        return moment(date).isBetween(from, "day", "[]");
      });

      if (dates.length === 0 || !dates[0]?.id) {
        toast.error("No diet plan found for the selected date.");
        setDietLoader(false);
        return;
      }
      setDietLoader(true);
      const docId = dates[0].id;
      const ref = doc(firestore, "dietplan", docId);
      const snapshot = await getDoc(ref);
      let data;
      let dietChartData = {};
      if (snapshot.exists()) {
        data = snapshot.data();

        let keys = Object.keys(data);
        for (let i = 0; i < keys.length; i++) {
          if (
            keys[i] !== "from" &&
            keys[i] !== "to" &&
            keys[i] !== "notes" &&
            keys[i] !== "clientId" &&
            keys[i] !== "clientName" &&
            keys[i] !== "createdAt" &&
            keys[i] !== "dietChartId" &&
            keys[i] !== "weekPlan" &&
            keys[i] !== "chartType"
          ) {
            if (Array.isArray(data[keys[i]])) {
              dietChartData[keys[i]] = [...data[keys[i]]];
            }
          }
        }
        dietChartData = {
          ...dietChartData,
        };

        setFormData({
          ...formData,
          weekPlan: data?.weekPlan
            ? { label: data.weekPlan, value: data.weekPlan }
            : null,
          chartType: data?.chartType
            ? { label: data.chartType, value: data.chartType }
            : null,
          notes: data?.notes,
        });

        const updatedDietPlan = {};

        schedules.forEach((schedule) => {
          const key = schedule.normalizedId || schedule.id;
          const items = data?.[key] || [];

          const updatedItems = items.map((item) => ({
            ...item,
            name:
              typeof item.name === "string"
                ? item.name
                : item.name?.label || item.name?.value || "",
            time: schedule.time,
          }));

          updatedDietPlan[key] = updatedItems;
        });

        setDietPlan(updatedDietPlan);
        setSelectedItem(dietChartData);
        setDietLoader(false);
      } else {
        setDietLoader(false);
        toast.error("Client not found!!!");
      }
    } catch (err) {
      setDietLoader(false);
      toast.error("Server error!!!");
    }
  }

  const getIncludesDates = useCallback(async () => {
    try {
      setDietLoader(true);
      const id = formData.clientName.value;
      const { data, note } = await getClientDietChartForDate(id);
      const datesToInclude = [];
      setIncludeDates(data);
      setNote(note);
      setFormData((e) => {
        e["notes"] = note;
        return e;
      });
      setDietLoader(false);
    } catch (err) {
      setDietLoader(false);
    }
  }, [formData?.clientName?.value]);

  useEffect(() => {
    if (formData?.clientName?.value) {
      getIncludesDates();
    }
  }, [getIncludesDates, formData?.clientName?.value]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];

    setFormData((prev) => ({
      ...prev,
      from: prev?.from || today,
      to: prev?.to || today,
    }));
    setMode("create");
  }, []);

  useEffect(() => {
    if (formData?.clientName?.value) {
      setDietPlan({ ...defaultDietObj });
      setSelectedItem(null);
      setSelectedTemplate({ label: "", value: {} });
      setSelectedDate(null);
      setIncludeDates(formData.clientName?.dietDates ?? []);
    } else {
      setIncludeDates([]);
    }
  }, [
    formData?.clientName,
    formData?.clientName?.dietDates,
    defaultDietObj,
    setSelectedItem,
  ]);

  const onChangeDate = (date) => {
    setNote("");
    setSelectedTemplate({ label: "", value: {} });
    setSelectedItem(null);
    if (date === null) return;
    if (!formData.clientName) {
      toast.error("Please fill Client Name field first");
      return;
    }
    if (date) {
      setSelectedDate(date);
      getClientDietChart(date);
    } else {
      setSelectedItem({});
      setSelectedDate("");
    }
  };

  const submitHandler = async (e) => {
    if (!formData.clientName) {
      toast.error("Please fill Client Name field");
      setErrorMessage({
        ...errorMessage,
        clientName: true,
      });
      return;
    }

    if (!formData.from) {
      toast.error("Please fill Diet start date");
      setErrorMessage({
        ...errorMessage,
        from: true,
      });
      return;
    }
    if (!formData.to) {
      toast.error("Please fill Diet end date");
      setErrorMessage({
        ...errorMessage,
        to: true,
      });
      return;
    }

    if (!formData.weekPlan) {
      toast.error("Please fill Week Plan field");
      setErrorMessage({
        ...errorMessage,
        weekPlan: true,
      });
      return;
    }

    if (!formData.chartType) {
      toast.error("Please fill Chart Type field");
      setErrorMessage({
        ...errorMessage,
        chartType: true,
      });
      return;
    }

    if (formData.from && formData.to) {
      const startDate = new Date(formData.from);
      const endDate = new Date(formData.to);

      if (startDate > endDate) {
        toast.error("Please make sure End Date is after the Start Date.");
        setErrorMessage({
          ...errorMessage,
          from: true,
          to: true,
        });
        return;
      }
    }

    return setSearchParams({ page: "make_diet_plan" });
  };

  return (
    <Card
      style={{ minHeight: "28vh", overflow: "inherit", borderRadius: "10px" }}
    >
      <SuiBox
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        alignItems="center"
        p={3}
        pb={1}
      >
        {formData?.clientName && !isServiceActive && !dietLoader && (
          <Alert
            severity="error"
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              bgcolor: "#FDECEA",
              color: "#611A15",
              border: "2px solid #F44336",
              borderRadius: 2,
              boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
              width: "100%",
            }}
          >
            This client does not have any active service. Please activate a
            service before creating a diet chart.
          </Alert>
        )}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={3}>
            <SuiBox lineHeight={1} mt={3}>
              <SuiTypography mb={1} variant="h6">
                Client Name*
              </SuiTypography>
              <Createable
                options={
                  open && editValue
                    ? [
                        {
                          label: client?.name + ` (+91${client?.phoneNumber})`,
                          value: client?.id,
                          name: client?.name,
                        },
                      ]
                    : clients ?? []
                }
                disabled={mode === "view"}
                error={errorMessage?.clientName}
                value={formData?.clientName}
                style={{ borderRadius: "0.5rem", fontSize: 14 }}
                inputStyle={{
                  width: 190,
                  height: "1.800rem",
                  borderRadius: "0.5rem",
                }}
                placeholder="Choose client"
                onChange={(e) => {
                  setSelectedTemplate({ label: "", value: {} });
                  setNote("");
                  setFormData({
                    ...formData,
                    clientName: e,
                  });
                  setClientActive({
                    clientName: e,
                  });
                  setIsClientTermsAccepted(e.isClientTermsAccepted);
                  setErrorMessage({
                    ...errorMessage,
                    clientName: false,
                  });
                  dispatch(getClientDetails({ id: e.key }));
                }}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} md={3}>
            <SuiBox lineHeight={1} mt={3}>
              <SuiTypography mb={1} variant="h6">
                Diet Start Date*
              </SuiTypography>
              <SuiInput
                disabled={mode === "view"}
                type="date"
                name="from"
                readOnly={mode === "view"}
                error={errorMessage?.from}
                value={formData?.from ?? selectedItem?.from ?? ""}
                onChange={(e) => {
                  const newFromDate = e.target.value;
                  const toDate = formData?.to;

                  if (newFromDate && toDate && newFromDate > toDate) {
                    setErrorMessage({
                      ...errorMessage,
                      from: true,
                      to: true,
                    });
                  } else {
                    setErrorMessage({
                      ...errorMessage,
                      from: false,
                      to: false,
                    });
                  }

                  setFormData({
                    ...formData,
                    [e.target.name]: e.target.value,
                  });
                }}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} md={3}>
            <SuiBox lineHeight={1} mt={3}>
              <SuiTypography mb={1} variant="h6">
                Diet End Date*
              </SuiTypography>
              <SuiInput
                disabled={mode === "view"}
                type="date"
                name="to"
                readOnly={mode === "view"}
                error={errorMessage?.to}
                value={formData?.to ?? selectedItem?.to ?? ""}
                onChange={(e) => {
                  const newToDate = e.target.value;
                  const fromDate = formData?.from;

                  if (fromDate && newToDate && fromDate > newToDate) {
                    setErrorMessage({
                      ...errorMessage,
                      from: true,
                      to: true,
                    });
                  } else {
                    setErrorMessage({
                      ...errorMessage,
                      from: false,
                      to: false,
                    });
                  }

                  setFormData({
                    ...formData,
                    [e.target.name]: e.target.value,
                  });
                }}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} md={3}>
            <SuiBox lineHeight={1} mt={3}>
              <SuiTypography mb={1} variant="h6">
                Week Plan*
              </SuiTypography>
              <Createable
                options={getWeeksArray() ?? []}
                disabled={mode === "view"}
                error={errorMessage?.weekPlan}
                value={formData?.weekPlan ?? ""}
                style={{ borderRadius: "0.5rem", fontSize: 14 }}
                inputStyle={{
                  width: 190,
                  height: "1.800rem",
                  borderRadius: "0.5rem",
                }}
                placeholder="Week plan"
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    weekPlan: e,
                  });

                  setErrorMessage({
                    ...errorMessage,
                    weekPlan: false,
                  });
                }}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <SuiBox lineHeight={1} mt={3}>
              <SuiTypography mb={1} variant="h6">
                Chart Type*
              </SuiTypography>
              <Createable
                options={chartTypes ?? []}
                disabled={mode === "view"}
                error={errorMessage?.chartType}
                value={formData?.chartType ?? ""}
                style={{ borderRadius: "0.5rem", fontSize: 14 }}
                inputStyle={{
                  width: 190,
                  height: "1.800rem",
                  borderRadius: "0.5rem",
                }}
                placeholder="Chart type"
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    chartType: e,
                  });

                  setErrorMessage({
                    ...errorMessage,
                    chartType: false,
                  });
                }}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} md={3}>
            <SuiBox lineHeight={1} mt={3}>
              <SuiTypography mb={1} variant="h6">
                Select Template
              </SuiTypography>
              <Createable
                isClearable
                options={dietChartTemplateNames}
                value={selectedTemplate}
                style={{ borderRadius: "0.5rem", fontSize: 14 }}
                inputStyle={{
                  width: 190,
                  height: "1.800rem",
                  borderRadius: "0.5rem",
                }}
                placeholder="Select Template"
                onChange={(e) => {
                  setSelectedDate(null);
                  setSelectedTemplate(e);
                  setSelectedItem((pre) => {
                    return {
                      ...pre,
                      ...e?.value?.formData,
                      weekPlan: e?.value?.weekPlan?.value,
                      chartType: e?.value?.chartType?.value,
                      notes: e?.value?.note,
                    };
                  });
                  setDietPlan((pre) => ({
                    ...pre,
                    ...e?.value?.dietPlan,
                  }));
                  setFormData((pre) => ({
                    ...pre,
                    weekPlan: e?.value?.weekPlan,
                    chartType: e?.value?.chartType,
                    notes: e?.value?.note,
                  }));
                  setNote(e?.value?.note);
                }}
              />
            </SuiBox>
          </Grid>

          {mode !== "view" && (
            <Grid item xs={12} md={2}>
              <SuiBox lineHeight={1} mt={3}>
                <SuiTypography mb={1} variant="h6">
                  Clone From
                </SuiTypography>
                <CustomeDatepicker
                  selectedDate={selectedDate}
                  onChangeDate={onChangeDate}
                  includeDates={includeDates}
                  placeholder={"Clone diet chart"}
                />
              </SuiBox>
            </Grid>
          )}

          {dietLoader && (
            <Grid
              item
              mb={1}
              xs={12}
              md={1}
              style={{ display: "flex", alignItems: "flex-end" }}
            >
              <SuiBox>
                <TailSpin color="#17c1e8" height={30} width={30} />
              </SuiBox>
            </Grid>
          )}
        </Grid>
        <SuiBox mb={3}>
          <SuiButton
            component={Button}
            color="dark"
            variant="gradient"
            onClick={submitHandler}
            width={180}
            disabled={!isServiceActive}
          >
            Plan Diet
            <ArrowForwardIcon style={{ fontSize: 16, marginLeft: 10 }} />
          </SuiButton>
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default PlanDetails;
